import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'
import { Message, MessageByUser } from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  messagesByOrderQuery,
  messagesByReviewQuery,
  messagesByToUser,
  onCreateMessageQuery,
  reviewMessageMentionsQuery,
} from '@/services/graphql'
import { onCreateMentionQuery } from '@/services/graphql/mention'

const client = getAmplifyClient()

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

const REVIEW_CONNECTION_PAGE_SIZE = 50

type MentionConnection = NonNullable<Message['mentions']> & {
  nextToken?: string | null
}

type ReviewMessagePageItem = Omit<Message, 'mentions'> & {
  mentions?: MentionConnection
}

export const getMessagesByReview = async (
  reviewID: string,
): Promise<Message[]> => {
  const items: ReviewMessagePageItem[] = []
  let nextToken: string | null = null

  do {
    const { data } = (await client.graphql({
      query: messagesByReviewQuery,
      variables: {
        reviewID,
        limit: REVIEW_CONNECTION_PAGE_SIZE,
        nextToken,
        sortDirection: 'ASC',
      },
    })) as GraphQLResult<{
      listMessageByReviewIDAndCreatedAt?: {
        items?: ReviewMessagePageItem[]
        nextToken?: string | null
      } | null
    }>
    const connection = data?.listMessageByReviewIDAndCreatedAt
    if (!connection) throw new Error('Review messages could not be loaded')
    items.push(...(connection.items ?? []))
    nextToken = connection.nextToken ?? null
  } while (nextToken)

  for (const message of items) {
    if (!message.mentions?.nextToken) continue

    const mentions = [...message.mentions.items]
    let mentionsNextToken: string | null = message.mentions.nextToken
    do {
      const { data } = (await client.graphql({
        query: reviewMessageMentionsQuery,
        variables: {
          id: message.id,
          limit: REVIEW_CONNECTION_PAGE_SIZE,
          nextToken: mentionsNextToken,
        },
      })) as GraphQLResult<{
        getMessage?: { mentions?: MentionConnection | null } | null
      }>
      const connection = data?.getMessage?.mentions
      if (!connection)
        throw new Error('Review message mentions could not be loaded')
      mentions.push(...(connection.items ?? []))
      mentionsNextToken = connection.nextToken ?? null
    } while (mentionsNextToken)
    message.mentions = { items: mentions, nextToken: null }
  }

  return items
}

export const useMessagesByOrder = ({ orderID }) => {
  return useQuery({
    queryKey: ['messagesByOrder', orderID],
    queryFn: async () => await messagesByOrder(orderID),
    enabled: !!orderID,
    placeholderData: [],
  })
}

const messagesByOrder = async (orderID: string) => {
  try {
    const items: Message[] = []
    let nextToken: string | null = null

    do {
      const { data } = (await client.graphql({
        query: messagesByOrderQuery,
        variables: {
          orderID,
          limit: 1000,
          nextToken,
        },
      })) as GraphQLResult<{
        messagesByOrder: { items: Message[]; nextToken?: string | null }
      }>

      items.push(...(data?.messagesByOrder?.items ?? []))
      nextToken = data?.messagesByOrder?.nextToken ?? null
    } while (nextToken)

    return items
  } catch (e) {
    throw normalizeError(e)
  }
}

export const useListMessagesByToUser = ({ toUserID, filter }) => {
  return useQuery({
    queryKey: ['messagesByUser', toUserID, filter],
    queryFn: async () => await listMessagesByToUser(toUserID, filter),
    enabled: !!toUserID,
    placeholderData: [],
  })
}

const listMessagesByToUser = async (toUserID: string, filter) => {
  try {
    const items: Message[] = []
    let nextToken: string | null = null

    do {
      const { data } = (await client.graphql({
        query: messagesByToUser,
        variables: {
          toUserID,
          filter,
          limit: 1000,
          nextToken,
        },
      })) as GraphQLResult<
        MessageByUser & {
          messagesByToUser?: {
            items?: Message[]
            nextToken?: string | null
          }
        }
      >

      items.push(...(data?.messagesByToUser?.items ?? []))
      nextToken = data?.messagesByToUser?.nextToken ?? null
    } while (nextToken)

    return items
  } catch (e) {
    throw normalizeError(e)
  }
}

export const onCreateMessage = ({ toUserID, subscriptionMessageCallback }) => {
  return (
    client.graphql({
      query: onCreateMessageQuery,
      variables: {
        toUserID,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    }) as unknown as { subscribe: Function }
  ).subscribe({
    next: ({ value }) => {
      subscriptionMessageCallback(value.data.onCreateMessage)
    },
    error: () => {
      // No-op
    },
  })
}

export const onCreateReviewMention = ({
  userMentionsId,
  subscriptionMentionCallback,
}: {
  userMentionsId: string
  subscriptionMentionCallback: (mention: {
    id: string
    createdAt?: string
    messageMentionsId?: string
    userMentionsId?: string
  }) => void
}) => {
  return (
    client.graphql({
      query: onCreateMentionQuery,
      variables: {
        userMentionsId,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    }) as unknown as { subscribe: Function }
  ).subscribe({
    next: ({ value }) => {
      subscriptionMentionCallback(value.data.onCreateMention)
    },
    error: () => {
      // No-op
    },
  })
}
